/**
 * Variant matrix (Phase 65 / BLU-594, tier-3).
 *
 * Rows are colours, columns are the union of sizes (catalogue order). Each cell
 * reflects the EXACT `derived` result already counted server-side: emerald tick
 * = derives, amber "!" = failed, muted dot = that colour/size combination isn't
 * in the catalogue. A colour-less product renders a single "No colour" row (its
 * pattern shows the `colourlessCode` segment); a size-less product renders a
 * single "One size" column — so a single-variant product reads as a clean 1x1.
 * A truncated variant list shows an honest banner (mirrors "never ready").
 *
 * `showStoreSku` (Phase 88, store SKU mode): each cell shows whether the
 * variant's store SKU is set up (a tick, "!" when it changed, an empty ring
 * when not set up yet), and the minted pattern column is dropped. Nothing is
 * derived for these products, so a derived pattern would be a SKU the store
 * never uses. The SKUs themselves sit behind a plain "Show SKUs" disclosure
 * above the table (BLU-1522 S6a, anyone can open it): opened, each cell shows
 * its store SKU as readable text, as before. Without the prop the output is
 * unchanged.
 *
 * BLU-1522 S8: a product with own base garments per colour or size shows
 * each variant's own product code (`derived.productCode`) in its colour's
 * pattern, one line per code when a colour's sizes sit on different base
 * garments. Without own base garments the patterns are the server's, as
 * before.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import type { DerivedDesignSet } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ColourDot } from './ColourDot';
import { HeldBackNote } from './HeldBackNote';
import { distinctColours, distinctSizes, isColourless } from '../lib/status';
import type { PreviewProduct, PreviewVariant } from '../lib/types';

interface VariantMatrixProps {
  product: PreviewProduct;
  /** Org's colour-less placeholder code, for the colour-less row's pattern. */
  colourlessCode: string;
  /** Store SKU mode: cells show each variant's store SKU and its set-up state. */
  showStoreSku?: boolean;
  /**
   * Phase 86 slice B (86-28): a BYO product's light, dark or medium per
   * variant id (the operator's choice, else the metafield's). Each colour's
   * pattern ends in the letter its variants will use, one line per letter
   * when its sizes differ. Absent: the server's pattern, unchanged.
   */
  byoVariantSets?: Record<string, DerivedDesignSet>;
}

type StoreSkuCellState = 'set_up' | 'changed' | 'not_set_up';

/** A variant's own store SKU state: the stored declared SKU equals the live SKU byte for byte, differs, or is absent. */
function storeSkuCellState(variant: PreviewVariant): StoreSkuCellState {
  if (!variant.declared) return 'not_set_up';
  return variant.declared.sku === variant.nativeSku ? 'set_up' : 'changed';
}

/** The screen-reader text after a store SKU cell's SKU, by its state. */
const STORE_SKU_CELL_SR: Record<StoreSkuCellState, string> = {
  set_up: 'tier3.cellStoreSkuSetUpSr',
  changed: 'tier3.cellStoreSkuChangedSr',
  not_set_up: 'tier3.cellStoreSkuNotSetUpSr',
};

/** A store SKU cell's name while the SKUs are hidden: its place and its state. */
const STORE_SKU_CELL_ARIA: Record<StoreSkuCellState, string> = {
  set_up: 'tier3.cellStoreSkuSetUpAria',
  changed: 'tier3.cellStoreSkuChangedAria',
  not_set_up: 'tier3.cellStoreSkuNotSetUpAria',
};

/** React key for the single colour-less row. */
const NO_COLOUR_KEY = '__nocolour__';
/** React key for the single "One size" column (distinct from the colour-less row key). */
const NO_SIZE_KEY = '__nosize__';

function variantKey(colour: string | null, size: string | null): string {
  return `${(colour ?? '').toLowerCase()}|${(size ?? '').toLowerCase()}`;
}

/** Render a derived pattern, highlighting the `{size}` placeholder in brand purple. */
function PatternCell({ pattern }: { pattern: string }) {
  const parts = pattern.split('{size}');
  if (parts.length === 1) {
    return <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">{pattern}</span>;
  }
  return (
    <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && <span className="font-semibold text-threadheads-purple">·size·</span>}
        </span>
      ))}
    </span>
  );
}

/** A pattern's product code segment (after an optional `CUST-` wrapper) swapped for `code`. */
function withProductCode(pattern: string, code: string): string {
  const custom = pattern.startsWith('CUST-');
  const body = custom ? pattern.slice('CUST-'.length) : pattern;
  const dash = body.indexOf('-');
  if (dash < 0) return pattern;
  return `${custom ? 'CUST-' : ''}${code}${body.slice(dash)}`;
}

/** A pattern's last letter (its print set) swapped for `letter`; unchanged when it does not end in `printSet`. */
function withSetLetter(pattern: string, printSet: string, letter: DerivedDesignSet): string {
  return pattern.endsWith(printSet) ? `${pattern.slice(0, -printSet.length)}${letter}` : pattern;
}

export function VariantMatrix({ product, colourlessCode, showStoreSku = false, byoVariantSets }: VariantMatrixProps) {
  const { t } = useTranslation('productSync');
  // Store SKU mode: the SKU text is opt-in, per row, and resets when the row closes.
  const [skusShown, setSkusShown] = useState(false);
  const tableId = useId();
  const colourless = isColourless(product);
  const sizes = distinctSizes(product);

  // Rows: real colours, or a single colour-less row (null). Columns: real
  // sizes, or a single "One size" column (null). null keys map to '' in the
  // lookup, matching how colour-less / size-less variants report their values.
  const rowColours: (string | null)[] = colourless ? [null] : distinctColours(product);
  const colSizes: (string | null)[] = sizes.length > 0 ? sizes : [null];

  const byKey = new Map<string, PreviewVariant>();
  const okByRow = new Map<string, number>();
  for (const v of product.variants) {
    byKey.set(variantKey(v.colourName, v.sizeName), v);
    if (showStoreSku ? storeSkuCellState(v) === 'set_up' : v.derived.ok) {
      const k = v.colourName ? v.colourName.toLowerCase() : NO_COLOUR_KEY;
      okByRow.set(k, (okByRow.get(k) ?? 0) + 1);
    }
  }

  const patternByColour = new Map(product.patterns.map((p) => [p.colourName.toLowerCase(), p]));

  // Phase 86 slice B: each row's letters, in variant order (BYO only).
  const lettersByRow = new Map<string, DerivedDesignSet[]>();
  if (byoVariantSets) {
    for (const v of product.variants) {
      const letter = byoVariantSets[v.id];
      if (!letter) continue;
      const k = !colourless && v.colourName ? v.colourName.toLowerCase() : NO_COLOUR_KEY;
      const list = lettersByRow.get(k) ?? [];
      if (!list.includes(letter)) list.push(letter);
      lettersByRow.set(k, list);
    }
  }

  // BLU-1522 S8: each row's own product codes, in variant order (only for a
  // product with own base garments, so every other product reads as before).
  const codesByRow = new Map<string, string[]>();
  if (product.ownBlankGroups) {
    for (const v of product.variants) {
      if (!v.derived.ok) continue;
      const k = !colourless && v.colourName ? v.colourName.toLowerCase() : NO_COLOUR_KEY;
      const list = codesByRow.get(k) ?? [];
      if (!list.includes(v.derived.productCode)) list.push(v.derived.productCode);
      codesByRow.set(k, list);
    }
  }

  // The colour-less row has no server pattern (patterns key off colour names),
  // so build a display pattern from the derivable segments. LOCKSTEP: the
  // trailing 'L' mirrors product-analysis.ts / design-sources.ts — colour-less
  // products use ONLY the single/light print set (no dark membership without a
  // colour), so their derived SKU always ends in 'L'. This is display-only; if
  // the backend ever emits a colour-less pattern in `patterns`, prefer it.
  const colourlessPattern = (): string => {
    const productCode = product.productType.code ?? '?';
    const segs = [productCode];
    if (sizes.length > 0) segs.push('{size}');
    segs.push(colourlessCode);
    return `${segs.join('-')}-${product.designCode}L`;
  };

  const colourLabel = (c: string | null) => c ?? t('tier3.noColour');
  const sizeLabel = (s: string | null) => s ?? t('tier3.oneSize');

  return (
    <div className="space-y-2">
      {showStoreSku && (
        <div className="flex justify-end">
          {/* The label says what a click does; the cells it rewrites are named by aria-controls. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
            aria-controls={tableId}
            onClick={() => setSkusShown((shown) => !shown)}
          >
            {skusShown ? t('tier3.hideSkus') : t('tier3.showSkus')}
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform duration-200 ease-out motion-reduce:transition-none', skusShown && 'rotate-180')}
              aria-hidden="true"
            />
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table id={tableId} className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-1 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('tier3.colour')}
              </th>
              {!showStoreSku && (
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('tier3.derivedPattern')}
                </th>
              )}
              {colSizes.map((size) => (
                <th key={size ?? NO_SIZE_KEY} className="px-2 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {sizeLabel(size)}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('tier3.check')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rowColours.map((colour) => {
              const rowKey = colour ? colour.toLowerCase() : NO_COLOUR_KEY;
              const serverPattern = colour ? patternByColour.get(rowKey) : undefined;
              const pattern = colour ? serverPattern?.pattern : colourlessPattern();
              const printSet = colour ? serverPattern?.printSet : 'L';
              const rowLetters = lettersByRow.get(rowKey) ?? [];
              const lettered =
                pattern && printSet && rowLetters.length > 0
                  ? rowLetters.map((letter) => withSetLetter(pattern, printSet, letter))
                  : pattern
                    ? [pattern]
                    : [];
              const rowCodes = codesByRow.get(rowKey) ?? [];
              const patterns =
                rowCodes.length > 0
                  ? [...new Set(rowCodes.flatMap((code) => lettered.map((p) => withProductCode(p, code))))]
                  : lettered;
              const okCount = okByRow.get(rowKey) ?? 0;
              return (
                <tr key={rowKey} className="border-b last:border-b-0">
                  <td className="px-1 py-1.5">
                    <span className={cn('flex items-center gap-2 text-[12.5px] font-semibold', !colour && 'text-muted-foreground')}>
                      <ColourDot name={colour} />
                      {colourLabel(colour)}
                    </span>
                  </td>
                  {!showStoreSku && (
                    <td className="px-2 py-1.5">
                      {patterns.length === 1 ? (
                        <PatternCell pattern={patterns[0]!} />
                      ) : patterns.length > 1 ? (
                        <span className="flex flex-col gap-0.5">
                          {patterns.map((p) => (
                            <PatternCell key={p} pattern={p} />
                          ))}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  )}
                  {colSizes.map((size) => {
                    const variant = byKey.get(variantKey(colour, size));
                    if (!variant) {
                      return (
                        <td key={size ?? NO_SIZE_KEY} className="px-2 py-1.5 text-center">
                          <span className="inline-block text-muted-foreground/40" aria-label={t('tier3.cellAbsentAria', { colour: colourLabel(colour), size: sizeLabel(size) })}>·</span>
                        </td>
                      );
                    }
                    if (showStoreSku && !skusShown) {
                      const state = storeSkuCellState(variant);
                      const label = t(STORE_SKU_CELL_ARIA[state], { colour: colourLabel(colour), size: sizeLabel(size) });
                      // Same marks as the derived cells (tick / "!"), plus an
                      // empty ring for "not set up yet" so it never reads as
                      // the absent dot. Each mark carries its name.
                      return (
                        <td key={size ?? NO_SIZE_KEY} className="px-2 py-1.5 text-center">
                          {state === 'set_up' ? (
                            <span role="img" className="inline-grid h-5 w-5 place-items-center rounded-full bg-emerald-100" aria-label={label}>
                              <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} aria-hidden="true" />
                            </span>
                          ) : state === 'changed' ? (
                            <span
                              role="img"
                              className="inline-grid h-5 w-5 place-items-center rounded-full bg-amber-100 text-[11px] font-bold text-amber-700"
                              aria-label={label}
                            >
                              <span aria-hidden="true">!</span>
                            </span>
                          ) : (
                            <span role="img" className="inline-block h-5 w-5 rounded-full border border-dashed border-muted-foreground/50 align-middle" aria-label={label} />
                          )}
                        </td>
                      );
                    }
                    if (showStoreSku) {
                      const state = storeSkuCellState(variant);
                      const sku = variant.nativeSku ?? t('tier1.storeSkuNone');
                      const where = t('tier3.cellStoreSkuWhereSr', { colour: colourLabel(colour), size: sizeLabel(size) });
                      // The SKU stays real text (read, selected and copied as
                      // is); the cell's place and its state ride beside it as
                      // screen-reader text, never by colour or icon alone.
                      return (
                        <td key={size ?? NO_SIZE_KEY} className="px-2 py-1.5 text-center">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[11px]',
                              state === 'set_up' && 'bg-emerald-50 text-emerald-800',
                              state === 'changed' && 'bg-amber-100 text-amber-800',
                              state === 'not_set_up' && 'text-muted-foreground',
                            )}
                          >
                            {state === 'set_up' && <Check className="h-3 w-3 shrink-0 text-emerald-700" strokeWidth={3} aria-hidden="true" />}
                            {state === 'changed' && <span className="font-sans font-bold" aria-hidden="true">!</span>}
                            <span>
                              <span className="sr-only">{`${where} `}</span>
                              {sku}
                              <span className="sr-only">{t(STORE_SKU_CELL_SR[state])}</span>
                            </span>
                          </span>
                        </td>
                      );
                    }
                    const ok = variant.derived.ok;
                    return (
                      <td key={size ?? NO_SIZE_KEY} className="px-2 py-1.5 text-center">
                        {ok ? (
                          <span
                            className="inline-grid h-5 w-5 place-items-center rounded-full bg-emerald-100"
                            aria-label={t('tier3.cellOkAria', { colour: colourLabel(colour), size: sizeLabel(size) })}
                          >
                            <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} aria-hidden="true" />
                          </span>
                        ) : (
                          <span
                            className="inline-grid h-5 w-5 place-items-center rounded-full bg-amber-100 text-[11px] font-bold text-amber-700"
                            aria-label={t('tier3.cellFailAria', { colour: colourLabel(colour), size: sizeLabel(size) })}
                          >
                            !
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right">
                    <span className={cn('text-[11px] font-bold tabular-nums', okCount > 0 ? 'text-emerald-700' : 'text-muted-foreground')}>
                      {showStoreSku ? t('tier3.rowSetUp', { count: okCount }) : t('tier3.rowValid', { count: okCount })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {product.variantsTruncated && <HeldBackNote>{t('tier3.truncatedBanner')}</HeldBackNote>}
    </div>
  );
}
